import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, FileDown, FileSpreadsheet, Save, Trash2, Plus, History } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fold = (s) => (s || "").toLocaleLowerCase("tr");
const VOTES = [
  ["biz", "Bize", "bg-[#0F2C59] text-white"],
  ["karsi", "Karşıya", "bg-[#b3122b] text-white"],
  ["belirsiz", "Belirsiz", "bg-amber-500 text-white"],
  ["gelmedi", "Gelmedi", "bg-slate-600 text-white"],
];
const COLOR_DOT = { mavi: "bg-blue-600", sari: "bg-yellow-400", "": "bg-slate-300" };

const download = async (path, name) => {
  const token = localStorage.getItem("imeak_token");
  const r = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) { toast.error("Dosya oluşturulamadı"); return; }
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

function MemberRow({ m, session, onSaved }) {
  const cell = m[session] || {};
  const [note, setNote] = useState(cell.note || "");
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState(false);
  useEffect(() => { setNote(cell.note || ""); }, [cell.note]);

  const save = async (vote, nextNote) => {
    setBusy(true);
    try {
      await api.put("/council/vote", {
        member_id: m.id, session, vote: vote ?? (cell.vote || ""), note: nextNote ?? note,
      });
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally { setBusy(false); }
  };

  return (
    <tr className="border-t border-border align-top" data-testid={`council-row-${m.seq}`}>
      <td className="px-2 py-2 text-xs text-muted-foreground">{m.seq}</td>
      <td className="px-2 py-2">
        <span className={`inline-block h-3 w-3 rounded-full ${COLOR_DOT[m.list_color] || COLOR_DOT[""]}`}
          title={m.list_color === "mavi" ? "MAVİ liste" : m.list_color === "sari" ? "SARI liste" : "Renk girilmedi"} />
      </td>
      <td className="px-2 py-2 text-xs">K{m.committee_no || "—"}</td>
      <td className="px-2 py-2 max-w-[280px]">
        <div className="text-sm font-semibold leading-tight">{m.person || "— temsilci yok —"}</div>
        <div className="text-xs text-muted-foreground leading-tight">{m.company_name}</div>
      </td>
      <td className="px-2 py-2">
        <div className="flex flex-wrap gap-1">
          {VOTES.map(([v, label, cls]) => (
            <button key={v} type="button" disabled={busy}
              onClick={() => save(cell.vote === v ? "" : v)}
              data-testid={`council-vote-${m.seq}-${v}`}
              className={`rounded-full px-2.5 py-1 text-xs font-bold transition-transform hover:scale-105 ${
                cell.vote === v ? cls : "bg-muted text-muted-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      </td>
      <td className="px-2 py-2 min-w-[200px]">
        <div className="flex gap-1">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not"
            className="h-8 text-xs" data-testid={`council-note-${m.seq}`} />
          {note !== (cell.note || "") && (
            <Button size="sm" className="h-8 px-2" disabled={busy} onClick={() => save(undefined, note)}
              data-testid={`council-note-save-${m.seq}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            </Button>
          )}
          {(cell.history?.length || 0) > 0 && (
            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setHist(!hist)}
              data-testid={`council-hist-${m.seq}`}><History className="h-3.5 w-3.5" /></Button>
          )}
        </div>
        {hist && (
          <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
            {cell.history.map((h, i) => (
              <li key={i}>{String(h.at).slice(0, 16).replace("T", " ")} · {h.by}: «{h.old_vote || "boş"}» → «{h.new_vote || "boş"}»</li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  );
}

function UnitResult({ r, onSaved }) {
  const [f, setF] = useState({
    our: r.our_votes ?? "", rival: r.rival_votes ?? "", invalid: r.invalid ?? "",
    seats: r.seats ?? "", lot: r.lottery_our_seats ?? "", note: r.note || "",
    winner: r.winner_manual ? r.winner : "auto",
  });
  const [busy, setBusy] = useState(false);
  const eff = f.winner === "auto"
    ? (f.our === "" || f.rival === "" ? null
      : Number(f.our) > Number(f.rival) ? "biz" : Number(f.rival) > Number(f.our) ? "karsi" : "kura")
    : f.winner;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/council/results/${r.unit}`, {
        our_votes: f.our === "" ? null : f.our, rival_votes: f.rival === "" ? null : f.rival,
        invalid: f.invalid === "" ? null : f.invalid, seats: f.seats === "" ? null : f.seats,
        winner: f.winner === "auto" ? null : f.winner,
        lottery_our_seats: f.lot === "" ? null : f.lot, note: f.note,
      });
      toast.success(`${r.label} sonucu kaydedildi`);
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 space-y-3" data-testid={`council-result-${r.unit}`}>
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-display font-bold">{r.label}</h4>
        {eff && (
          <span className={`rounded-full px-2.5 py-1 text-xs font-bold text-white ${
            eff === "biz" ? "bg-[#0F2C59]" : eff === "karsi" ? "bg-[#b3122b]" : "bg-orange-500"}`}>
            {eff === "biz" ? "BİZDE" : eff === "karsi" ? "RAKİP" : "Beraberlik · kura"}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[["our", "Bizim oy"], ["rival", "Karşı oy"], ["invalid", "Geçersiz"], ["seats", "Koltuk"]].map(([k, lb]) => (
          <label key={k} className="text-xs font-semibold text-muted-foreground">
            {lb}
            <Input type="number" min="0" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
              className="h-9 mt-1" data-testid={`council-${r.unit}-${k}`} />
          </label>
        ))}
      </div>
      {eff === "kura" && (
        <div className="rounded-lg border border-orange-300 bg-orange-50 p-3 dark:bg-orange-950/30">
          <label className="text-xs font-bold text-orange-800 dark:text-orange-200">
            Kurada bize düşen üye sayısı
            <Input type="number" min="0" value={f.lot} onChange={(e) => setF({ ...f, lot: e.target.value })}
              className="h-9 mt-1" data-testid={`council-${r.unit}-lot`} />
          </label>
          {f.seats !== "" && f.lot !== "" && (
            <p className="mt-1 text-xs text-orange-800 dark:text-orange-200">
              Bize {f.lot} · rakibe {Math.max(Number(f.seats) - Number(f.lot), 0)} koltuk
            </p>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[140px]">
          <span className="text-xs font-semibold text-muted-foreground">Kazanan</span>
          <Select value={f.winner} onValueChange={(v) => setF({ ...f, winner: v })}>
            <SelectTrigger className="h-9 mt-1" data-testid={`council-${r.unit}-winner`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Otomatik</SelectItem>
              <SelectItem value="biz">Bizde</SelectItem>
              <SelectItem value="karsi">Rakip</SelectItem>
              <SelectItem value="kura">Kura</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Not"
          className="h-9 flex-1 min-w-[160px]" data-testid={`council-${r.unit}-note`} />
        <Button onClick={save} disabled={busy} data-testid={`council-${r.unit}-save`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet
        </Button>
        {r.updated_at && (
          <Button variant="ghost" size="sm" onClick={async () => {
            await api.delete(`/council/results/${r.unit}`); toast.success("Sonuç silindi"); await onSaved();
          }} data-testid={`council-${r.unit}-clear`}><Trash2 className="h-4 w-4" /></Button>
        )}
      </div>
      {r.updated_at && (
        <p className="text-[11px] text-muted-foreground">
          Son güncelleme: {String(r.updated_at).slice(0, 16).replace("T", " ")} · {r.updated_by}
        </p>
      )}
    </Card>
  );
}

export default function CouncilTally() {
  const [data, setData] = useState(null);
  const [results, setResults] = useState([]);
  const [session, setSession] = useState("s1");
  const [q, setQ] = useState("");
  const [color, setColor] = useState("all");
  const [status, setStatus] = useState("all");
  const [adding, setAdding] = useState(false);
  const [nf, setNf] = useState({ committee_no: "", company_name: "", person: "", list_color: "sari", group_name: "" });

  const load = useCallback(async () => {
    const [m, r] = await Promise.all([api.get("/council/members"), api.get("/council/results")]);
    setData(m.data); setResults(r.data.results);
  }, []);
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e.response?.data?.detail, e))); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const term = fold(q.trim());
    return data.members.filter((m) => {
      if (color !== "all" && (m.list_color || "none") !== color) return false;
      const v = m[session]?.vote || "";
      if (status === "isaretsiz" && v) return false;
      if (status !== "all" && status !== "isaretsiz" && v !== status) return false;
      if (!term) return true;
      return [m.company_name, m.person, m.group_name, String(m.committee_no)].some((x) => fold(x).includes(term));
    });
  }, [data, q, color, status, session]);

  if (!data) {
    return <Card className="p-8 text-center" data-testid="council-loading"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></Card>;
  }

  const s = data.summary.sessions[session];
  const c = s.counts;
  const cards = [
    ["Toplam üye", data.summary.total, "text-foreground"],
    ["MAVİ (biz)", data.summary.mavi, "text-blue-600"],
    ["SARI (karşı)", data.summary.sari, "text-yellow-600"],
    ["Bize oy", c.biz, "text-[#0F2C59]"],
    ["Karşıya oy", c.karsi, "text-[#b3122b]"],
    ["Belirsiz", c.belirsiz, "text-amber-600"],
    ["Gelmedi", c.gelmedi, "text-slate-500"],
    ["İşaretsiz", c.isaretsiz, "text-muted-foreground"],
  ];

  const addMember = async () => {
    try {
      await api.post("/council/members", nf);
      toast.success("Üye eklendi");
      setAdding(false);
      setNf({ committee_no: "", company_name: "", person: "", list_color: "sari", group_name: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, e)); }
  };

  return (
    <div className="space-y-4" data-testid="council-tally">
      <div className="flex flex-wrap gap-2">
        {Object.entries(data.session_labels).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setSession(k)} data-testid={`council-session-${k}`}
            className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
              session === k ? "bg-[#b3122b] text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="council-summary">
        {cards.map(([label, val, cls]) => (
          <Card key={label} className="p-3">
            <div className="text-[11px] font-semibold text-muted-foreground">{label}</div>
            <div className={`font-display text-2xl font-bold ${cls}`}>{val}</div>
          </Card>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Salonda oy kullanan: <b>{s.present}</b> / {data.summary.total} · çoğunluk için gereken: {s.needed}
      </p>

      <div className="grid gap-3 md:grid-cols-2">
        {results.filter((r) => r.session === session).map((r) => (
          <UnitResult key={r.unit} r={r} onSaved={load} />
        ))}
      </div>

      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Temsilci, şirket, komite ara"
              className="h-9 pl-8" data-testid="council-search" />
          </div>
          <Select value={color} onValueChange={setColor}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="council-filter-color"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm listeler</SelectItem>
              <SelectItem value="mavi">MAVİ liste</SelectItem>
              <SelectItem value="sari">SARI liste</SelectItem>
              <SelectItem value="none">Renk girilmedi</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-[160px]" data-testid="council-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm işaretler</SelectItem>
              <SelectItem value="biz">Bize oy verdi</SelectItem>
              <SelectItem value="karsi">Karşıya oy verdi</SelectItem>
              <SelectItem value="belirsiz">Belirsiz</SelectItem>
              <SelectItem value="gelmedi">Gelmedi</SelectItem>
              <SelectItem value="isaretsiz">İşaretsiz</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => download(`/council/export?format=pdf&session=${session}`, "meclis_cetelesi.pdf")}
            data-testid="council-pdf"><FileDown className="h-4 w-4" /> PDF</Button>
          <Button variant="outline" onClick={() => download("/council/export?format=xlsx&session=all", "meclis_cetelesi.xlsx")}
            data-testid="council-xlsx"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
          <Button variant="outline" onClick={() => setAdding(!adding)} data-testid="council-add-toggle">
            <Plus className="h-4 w-4" /> Üye ekle
          </Button>
        </div>
        {adding && (
          <div className="mt-3 grid gap-2 sm:grid-cols-5" data-testid="council-add-form">
            <Input placeholder="Komite no" value={nf.committee_no} onChange={(e) => setNf({ ...nf, committee_no: e.target.value })} data-testid="council-add-cno" />
            <Input placeholder="Şirket unvanı" value={nf.company_name} onChange={(e) => setNf({ ...nf, company_name: e.target.value })} data-testid="council-add-company" />
            <Input placeholder="Temsilci" value={nf.person} onChange={(e) => setNf({ ...nf, person: e.target.value })} data-testid="council-add-person" />
            <Select value={nf.list_color} onValueChange={(v) => setNf({ ...nf, list_color: v })}>
              <SelectTrigger data-testid="council-add-color"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mavi">MAVİ</SelectItem>
                <SelectItem value="sari">SARI</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={addMember} data-testid="council-add-save">Ekle</Button>
          </div>
        )}
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="council-table">
          <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-2 py-2">#</th><th className="px-2 py-2">Liste</th><th className="px-2 py-2">K</th>
              <th className="px-2 py-2">Üye / Şirket</th><th className="px-2 py-2">Oy</th><th className="px-2 py-2">Not</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => <MemberRow key={m.id} m={m} session={session} onSaved={load} />)}
          </tbody>
        </table>
        {rows.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground" data-testid="council-empty">Kayıt bulunamadı</p>}
      </Card>
      <p className="text-xs text-muted-foreground">{rows.length} üye listeleniyor</p>
    </div>
  );
}
